import { useState } from 'react';
import { cn } from '~/util';
import { useFieldContext } from '~/util/hooks/useFormContext';
import { BaseLevel } from '~/util/types/levels';
import { LevelsSelector } from '../../ItemSelector';
import {
    FieldLabelWithError,
    FieldWithErrorProps,
} from './FieldLabelWithError';
import { useDisableFormContext } from '../DisableContext';
import { InteractableDataWrapper } from '~/partials/InteractableDataWrapper';
import { LevelTags, TagsBase } from '~/partials/Tags';

type LevelFieldOptions = Partial<
    Omit<TagsBase<BaseLevel>, 'items' | 'editable' | 'onEdit' | 'item'>
>;

export function LevelField({
    list,
    placeholder,
    className,
    forceDisabled,
    tagOptions = {},
    ...labelProps
}: {
    list: 'classic' | 'platformer';
    placeholder?: string;
    forceDisabled?: boolean;
    tagOptions?: LevelFieldOptions;
    className?: string;
} & FieldWithErrorProps) {
    const field = useFieldContext<BaseLevel | null>();
    const [levelSelectorOpen, setLevelSelectorOpen] = useState(false);
    const disabled = useDisableFormContext() || forceDisabled;
    return (
        <FieldLabelWithError<BaseLevel | null> {...labelProps}>
            <InteractableDataWrapper
                id={field.name}
                required={labelProps.required}
                disabled={disabled}
                open={levelSelectorOpen}
                className={cn(
                    field.state.value
                        ? 'text-secondary-foreground'
                        : 'text-secondary-light',
                    className,
                    disabled && 'opacity-50',
                )}
                onClick={() => {
                    if (!disabled) setLevelSelectorOpen(true);
                }}
            >
                <p className="text-ellipsis text-nowrap overflow-clip w-11/12">
                    {field.state.value?.name ?? placeholder}
                </p>
            </InteractableDataWrapper>
            <LevelsSelector
                open={levelSelectorOpen}
                onOpenChange={setLevelSelectorOpen}
                initial={field.state.value ?? undefined}
                single
                list={list}
                onSave={(items) => {
                    const item = items instanceof Array ? items[0] : items;
                    field.handleChange(item ?? null);
                    setLevelSelectorOpen(false);
                }}
                tagOptions={tagOptions}
            />
        </FieldLabelWithError>
    );
}

export function LevelsField({
    list,
    placeholder,
    tagOptions = {},
    forceDisabled,
    ...labelProps
}: {
    list: 'classic' | 'platformer';
    placeholder?: string;
    forceDisabled?: boolean;
    tagOptions?: LevelFieldOptions;
} & FieldWithErrorProps) {
    const field = useFieldContext<BaseLevel[] | null>();
    const [levelSelectorOpen, setLevelSelectorOpen] = useState(false);
    const disabled = useDisableFormContext() || forceDisabled;
    return (
        <FieldLabelWithError<BaseLevel[] | null> {...labelProps}>
            <LevelTags
                items={field.state.value ?? []}
                editable={!disabled}
                onEdit={() => setLevelSelectorOpen(true)}
                highContrast
                placeholder={placeholder}
                onRemove={({ id }) =>
                    field.handleChange(
                        field.state.value?.filter((item) => item.id != id) ??
                            null,
                    )
                }
                {...tagOptions}
            />
            <LevelsSelector
                list={list}
                open={levelSelectorOpen}
                onOpenChange={setLevelSelectorOpen}
                initial={field.state.value ?? []}
                onSave={(items) => {
                    field.handleChange(
                        (items instanceof Array ? items : [items]) ?? null,
                    );
                    setLevelSelectorOpen(false);
                }}
                tagOptions={tagOptions}
            />
        </FieldLabelWithError>
    );
}
